import {
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DiagnosisRow } from "../../api/diagnosis.type";
import { useCreateContent } from "../../hooks/useCreateContent";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import ThemedSpinner from "../shared/spinner";
import Typography from "../shared/typography";
import { formatTimeStampToHumanReadableDate } from "../utls/time";
import { cn } from "../utls/utils";

interface DiagnosisItemProps {
  diagnosis: DiagnosisRow;
}

export default function DiagnosisItem({ diagnosis }: DiagnosisItemProps) {
  const {
    diagnosis_code,
    diagnosis_description,
    diagnosis_details,
    notes,
    severity,
    status,
    effective_from,
    effective_to,
    created_at,
    name,
  } = diagnosis as DiagnosisRow;

  const navigate = useNavigate();
  const { createContent, isPending, data } = useCreateContent();

  const renderDate = (iso?: string | null) => {
    if (!iso || iso === "infinity") return null;
    return formatTimeStampToHumanReadableDate(iso);
  };

  const detailsEntries =
    diagnosis_details && typeof diagnosis_details === "object"
      ? Object.entries(diagnosis_details as Record<string, any>)
      : [];

  useEffect(() => {
    if (data) {
      navigate(`/content/${data.slug}`);
    }
  }, [data]);

  return (
    <Card variant="cream" elevation={1} className="w-full">
      <Card.Body className="space-y-1 grid grid-cols-2 gap-x-4 lg:gap-x-2 gap-y-2 lg:grid-cols-4">
        <div>
          {status && (
            <Typography
              variant="body2"
              className={cn(
                status.toLowerCase() === "active"
                  ? "text-green-500"
                  : "text-brown-800"
              )}
            >
              Status: {status}
            </Typography>
          )}
          <div className="mt-1 flex flex-col gap-1">
            {renderDate(effective_from) && (
              <Typography variant="caption" className="text-brown-600">
                Effective From: {renderDate(effective_from)}
              </Typography>
            )}
            {renderDate(created_at) && (
              <Typography variant="caption" className="text-brown-600">
                Added: {renderDate(created_at)}
              </Typography>
            )}
            {renderDate(effective_to) && (
              <Typography variant="caption" className="text-brown-600">
                Effective To: {renderDate(effective_to)}
              </Typography>
            )}
          </div>
        </div>
        <div>
          {severity && (
            <Typography
              variant="caption"
              className={cn(
                "font-semibold uppercase",
                severity === "critical" ? "text-red-500" : "text-brown-600"
              )}
            >
              Severity: {severity}
            </Typography>
          )}
          {name && (
            <Typography variant="subtitle1" className="text-brown-800">
              {name}
            </Typography>
          )}
          {diagnosis_code && (
            <Typography variant="body2" className="text-brown-800">
              Code: {diagnosis_code}
            </Typography>
          )}

          {notes && (
            <Typography variant="body2" className="text-brown-800">
              Notes: {notes}
            </Typography>
          )}
          {detailsEntries.map(([key, value]) => (
            <Typography key={key} variant="body2" className="text-brown-800">
              {key}: {String(value)}
            </Typography>
          ))}
        </div>
      </Card.Body>
      <Card.Footer
        variant="cream"
        className="flex flex-col lg:flex-row justify-end gap-2 lg:gap-4"
      >
        <div className="flex flex-col lg:flex-col  gap-2 lg:gap-4">
          <SubmitButton
            size="sm"
            color="cream"
            onClick={() =>
              createContent({
                input: `Gather information about ${
                  name || diagnosis_description
                }. Search for relevant details on diagnosis methods, treatment, prognosis and more.Your response should be an markdown format. Use the web search to find the most recent information and cite your sources.`,
                diagnosisId: diagnosis.id,
              })
            }
            icon={
              isPending ? (
                <ThemedSpinner size="sm" />
              ) : (
                <InformationCircleIcon className="w-5 h-5 text-brown-800" />
              )
            }
          >
            More Info
          </SubmitButton>

          <SubmitButton
            size="sm"
            icon={<QuestionMarkCircleIcon className="w-5 h-5 text-cream-500" />}
            onClick={() =>
              navigate(
                `/chat?question=${encodeURIComponent(
                  `What does ${diagnosis_description} mean?`
                )}`
              )
            }
          >
            Ask Question
          </SubmitButton>
        </div>
      </Card.Footer>
    </Card>
  );
}
